import { computed, getCurrentScope, onScopeDispose, ref, shallowRef, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { getAccessToken, getAuthMode } from '../../services/auth'
import { fetchAgentRunEvents, type AgentRunEvent } from '../services/strategyApi'

export const RECONNECT_MS = 1500
const FINAL_STATUSES = new Set(['succeeded', 'stopped', 'failed', 'cancelled'])
const OPEN_TOOL_PHASES = new Set(['start', 'working', 'new'])

export function isFinalEvent(e: AgentRunEvent): boolean {
  return e.kind === 'system_log' && FINAL_STATUSES.has(String(e.payload?.status ?? ''))
}

// Tools log a start and a done line per call; count each call once.
export function isToolCall(e: AgentRunEvent): boolean {
  return e.kind === 'tool' && !OPEN_TOOL_PHASES.has(String(e.payload?.tool?.phase ?? ''))
}

export function streamUrl(runId: string, after: string | null, token: string | null): string {
  const params = new URLSearchParams()
  if (getAuthMode() === 'legacy' && token) params.set('token', token)
  if (after) params.set('after', after)
  const q = params.toString()
  return `/api/agent_runs/${encodeURIComponent(runId)}/events/stream${q ? `?${q}` : ''}`
}

// Folds only the events added since the last read; a list that doesn't extend the last one starts over.
export function useEventFold<T>(
  events: MaybeRefOrGetter<readonly AgentRunEvent[]>,
  init: () => T,
  step: (acc: T, e: AgentRunEvent) => T,
): ComputedRef<T> {
  let seen: readonly AgentRunEvent[] = []
  let acc = init()
  return computed(() => {
    const list = toValue(events)
    const extends_ = list.length >= seen.length && (!seen.length || list[seen.length - 1] === seen[seen.length - 1])
    if (!extends_) acc = init()
    for (let i = extends_ ? seen.length : 0; i < list.length; i++) acc = step(acc, list[i])
    seen = list
    return acc
  })
}

// One run's events: the last 200, then the live stream until the run's final event.
export function useAgentRunEvents(runId: MaybeRefOrGetter<string | null | undefined>) {
  const events = shallowRef<AgentRunEvent[]>([])
  const done = ref(false)
  const latestProgress = ref<string | null>(null)
  const toolCalls = ref(0)
  let seen = new Set<string>()
  let es: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let generation = 0

  function close() {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    reconnectTimer = null
    es?.close()
    es = null
  }

  function add(list: AgentRunEvent[]) {
    const fresh = list.filter((e) => e?.id && !seen.has(e.id))
    if (!fresh.length) return
    for (const e of fresh) {
      seen.add(e.id)
      if (isFinalEvent(e)) done.value = true
      if (e.kind === 'progress' && e.message) latestProgress.value = e.message
      if (isToolCall(e)) toolCalls.value++
    }
    events.value = [...events.value, ...fresh]
  }

  function open(id: string, gen: number) {
    close()
    const token = getAccessToken()
    if (gen !== generation || done.value || !token) return
    const last = events.value[events.value.length - 1]
    const source = new EventSource(streamUrl(id, last?.id ?? null, token))
    es = source
    source.onmessage = (msg) => {
      let parsed: AgentRunEvent | null = null
      try {
        parsed = JSON.parse(msg.data)
      } catch {
        return
      }
      if (!parsed?.id) return
      add([parsed])
      if (isFinalEvent(parsed)) close()
    }
    source.onerror = () => {
      close()
      if (gen !== generation) return
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        open(id, gen)
      }, RECONNECT_MS)
    }
  }

  async function start(id: string | null | undefined) {
    const gen = ++generation
    close()
    seen = new Set()
    events.value = []
    done.value = false
    latestProgress.value = null
    toolCalls.value = 0
    if (!id) return
    try {
      const resp = await fetchAgentRunEvents(id)
      if (gen !== generation) return
      add(resp.events ?? [])
    } catch {
      // The stream replays from the start when the first read fails.
      if (gen !== generation) return
    }
    open(id, gen)
  }

  function stop() {
    generation++
    close()
  }

  watch(() => toValue(runId), (id) => { void start(id) }, { immediate: true })
  if (getCurrentScope()) onScopeDispose(stop)

  return { events, done, latestProgress, toolCalls }
}
